<template>
  <div class="container-lg">
    <div class="generic-listing" ref="top">
      <div class="row">

        <!-- keyword search -->
        <div v-if="showKeywordSearch" 
             :class="'col-12 col-lg-9 generic-listing-controls pt-3 pt-lg-0 pe-lg-5 px-0 px-lg-gutter' + (isLoading?' inactive':'')">
          <div class="block">
            <div class="container px-lg-0">
              <p><slot name="document-listing-initial-text"></slot></p>
              <div class="input-group mb-3">
                <input :value="currentQuery.keyword"
                       type="text" 
                       class="search-form-input form-control border-1 fs-large bg-transparent" 
                       :placeholder="keywordPlaceholder"
                       ref="keyword"
                       @keyup.enter="onKeywordChange" />
                <button type="button" @click="onKeywordChange" class="btn btn-primary">
                  <slot name="search"></slot>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- sidebar controls -->
        <div v-if="categories.items"
             :class="'col-12 col-lg-3 order-lg-first generic-listing-controls pt-3 pt-lg-0 pe-lg-5 px-0 px-lg-gutter' + (isLoading?' inactive':'')">
          <div class="block"><!-- other filters -->
            <div class="container px-lg-0">
              <div :class="`d-flex flex-lg-column ` + controlClasses">
                <generic-filter :hideEmptyGroup="true" :filterGroups="filterGroups" :results="filteredItems" :currentQuery="currentQuery" :showItemCount="false" :navLinks="navLinks" @filterChange="onFilterChange">
                  <template v-for="(_, name) in $slots" v-slot:[name]="slotData"><slot :name="name" v-bind="slotData" /></template>
                </generic-filter>
              </div>
            </div>
          </div>
          <div v-if="isKeywordEnabled" class="block"><!-- keyword clear -->
            <div v-if="currentQuery.keyword" class="container d-flex align-items-center justify-content-between px-lg-0">
              <div class="category-filter-count-wrap fw-bold fs-eyebrow">
                <span>"{{ currentQuery.keyword }}"</span>
              </div>
              <div class="bg-transparent border-0 ps-0 py-2 py-lg-1 link-black text-decoration-underline">
                <button type="button" class="bg-transparent border-0 ps-0 py-2 py-lg-1 link-black text-decoration-underline" @click="currentQuery.keyword=''">
                  <slot name="keyword-clear"></slot>
                </button>
              </div>
            </div>
          </div>          
          <div v-if="!currentQuery.keyword" class="block bg-light bg-lg-transparent"><!-- category filter -->
            <div class="container px-lg-0 pt-4 pt-lg-0">
              <category-filter :filterData="categories" :results="filteredItems" :currentQuery="currentQuery" :showCount="true" @categoryChange="onCategoryChange">
                <template v-for="(_, name) in $slots" v-slot:[name]="slotData"><slot :name="name" v-bind="slotData" /></template>
              </category-filter>
            </div>
          </div>
        </div>

        <!-- results -->
        <div class="col generic-listing-results pt-3 pt-lg-0" aria-live="polite">
          <div class="container px-lg-0">
            <div :class="`row ` + listingRowClasses">
              <div v-if="isLoading">
                <slot name="document-listing-loading-text"></slot>
              </div>
              <div v-if="showResults" :class="columnClasses">
                <p v-if="0==filteredItems.length">
                  <slot name="document-listing-no-results-text"></slot>
                </p>
                <div class="row">
                  <component v-for="item in paginatedItems" :is="resultComponent" :item="item">
                    <template v-for="(_, name) in $slots" v-slot:[name]="slotData"><slot :name="name" v-bind="slotData" /></template>
                  </component>
                </div>
              </div>
            </div>
            <div v-if="!isLoading && moreItems" ref="moreTrigger" id="generic-listing-more">
              <button type="button" class="btn btn-primary" @click="loadMore"><slot name="document-listing-load-more-text"></slot></button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>
<script>
  import CategoryFilter from './document-listing/category-filter.vue';
  import GenericFilter from './generic-listing/generic-filter.vue';
  import DocumentResult from './generic-listing/listing-items/document-result.vue'
  import ImageResult from './generic-listing/listing-items/image-result.vue';
  import ContentAssetLibraryResult from './generic-listing/listing-items/content-asset-library-result.vue';

  export default {
    components: {
      'category-filter': CategoryFilter,
      'generic-filter': GenericFilter,
      'document-result': DocumentResult,
      'image-result': ImageResult,
      'content-asset-library-result': ContentAssetLibraryResult
    },
    data() {
      let searchParams = new URLSearchParams(window.location.search);
      let d = window.wf.docListing;
      d.filterGroups.forEach((grp) => {
        if (!d.currentQuery.filters[grp.id] || d.currentQuery.filters[grp.id].length == 0) {
          d.currentQuery.filters[grp.id] = searchParams.get(`filter_${grp.id}`) ? searchParams.get(`filter_${grp.id}`).split(';') : [];
        }
        // ensure all items have at least an empty filter
        d.items.forEach((item) => {
          if (!item.filterValues[grp.id]) {
            item.filterValues[grp.id] = [];
          }
        })
      })
      if (searchParams.get("category")) {
        d.currentQuery.category = searchParams.get("category");
      }
      if(this.isKeywordEnabled && searchParams.get("keyword")) {
        d.currentQuery.keyword = searchParams.get("keyword");
      }
      d.resultComponent ??= 'document-result';
      d.controlClasses ??= "justify-content-end";
      d.itemsPerPage ??= 10;
      d.paginationState ??= {
        itemsShown: d.itemsPerPage
      }

      d.rest ??= 'docs';
      d.server ??= false;
      d.showUnfilteredResults ??= false;
      d.navLinks ??= [];
      d.isLoading = false;
      d.isProcessing = false;

      return d;
    },
    computed: {
      keywordPlaceholder() {
        // there has to be a better way
        return this.$slots['document-listing-search-label']()[0].children;
      },
      filteredItems() {
        return this.items;
      },
      paginatedItems() {
        let filtered = this.filteredItems;
        return filtered.slice(0, Math.min(filtered.length, Math.max(0, this.paginationState.itemsShown)));
      },
      showResults() {
        // never show results during loading
        if(this.isLoading) return false;

        // if don't require a filter, just show results
        if(this.showUnfilteredResults) return true;

        // if keyword or category filtering, show results
        if(this.currentQuery.category || this.currentQuery.keyword) return true;

        // if doctype filtering, show results
        if(Array.isArray(this.currentQuery.filters.docType) && 0 < this.currentQuery.filters.docType.length) return true;

        // default to not show
        return false;
      },
      moreItems() {
        if(!this.showResults) return false;

        const itemsExist = this.filteredItems && 0 < this.filteredItems.length;
        const moreItems = itemsExist && this.paginatedItems.length < this.filteredItems.length;

        return moreItems ? true : false;
      },     
      categoryItems() {
        return this.items;
      },
      isKeywordEnabled() {
        // if applicable to current listing
        return (typeof this.currentQuery.keyword !== 'undefined');
      },      
      showKeywordSearch() {
        // don't show if showing results
        if(this.showResults) return false;
        
        // don't show if keyword attribute doesn't exist, which means it's not applicable to current listing
        // and, don't show if keyword already entered
        if("" !== this.currentQuery.keyword) return false;

        // don't show if category selected
        if(this.currentQuery.category) return false;

        // don't show if loading
        if(this.isLoading) return false;

        return true;
      }      
    },
    watch: {
      currentQuery: {
        handler: function(newVal, oldVal) {
          this.updateLocation();
          this.doSearch();
          this.paginationState.itemsShown = this.itemsPerPage;
        },
        deep: true
      }
    },
    methods: {
      updateLocation() {
        let queryString = new URLSearchParams();
        this.filterGroups.forEach((grp) => {
          if (this.currentQuery.filters[grp.id] && this.currentQuery.filters[grp.id].length) {
            queryString.append(`filter_${grp.id}`, this.currentQuery.filters[grp.id].join(';'))
          }
        })
        if(this.currentQuery.category) queryString.append("category", this.currentQuery.category);
        if(this.currentQuery.keyword) queryString.append("keyword", this.currentQuery.keyword);
        window.history.replaceState(window.history.state, null, `${window.location.pathname}?${queryString.toString()}`);
      },
      checkLoadMore() {
        let scrollTop = document.scrollingElement.scrollTop + window.innerHeight;
        let moreOffsetTop = 0;
        let test = this.$refs.moreTrigger;
        while (test) {
          moreOffsetTop += test.offsetTop;
          test = test.offsetParent;
        }
        if (moreOffsetTop <= scrollTop) {
          this.loadMore();    
        }
      },
      loadMore() {
        this.paginationState.itemsShown += this.itemsPerPage
      },
      loadFilteredItems() {
        this.isLoading = true;
        this.isProcessing = true;

        let query = '?';

        let c = this.currentQuery.category ? this.currentQuery.category : '';
        query += 'c=' + c;

        let t = this.currentQuery.filters.docType ? this.currentQuery.filters.docType.join('|') : ''
        query += '&t=' + t;

        let m = this.currentQuery.filters.model ? this.currentQuery.filters.model.join('|') : ''
        query += '&m=' + m;

        let k = this.currentQuery.keyword ? this.currentQuery.keyword : '';
        query += '&k=' + k;

        let endpoint = `${window.location.origin}/${this.culture}/${this.rest}/`;
        if(this.rest == 'images') endpoint += `${this.nodeid}/`;
        endpoint += `${query}`;

        fetch(endpoint)
          .then(response => response.json())
          .then(data => {
            this.items = data.items;
            this.filterGroups = data.filterGroups;
            this.totalItems = data.totalItems;
            this.isLoading = false;
            this.isProcessing = false;
          })
          .finally(() => {

          });
      },
      onCategoryChange() {
        this.clearFilters();
      },
      onKeywordChange(event) {
        this.currentQuery.keyword = this.$refs.keyword.value.trim(' ');
      },
      onFilterChange() {
        this.goto('top');
      },      
      doSearch() {
        if('localhost' == window.location.hostname) {
          // For breakouts don't reload from endpoint
        }
        else {
         this.loadFilteredItems(); 
        }

      },
      clearFilters() {
        this.currentQuery.filters.model = [];
        this.currentQuery.filters.docType = [];
      },
      goto(refName) {
        var element = this.$refs[refName];
        var top = element.offsetTop;

        window.scrollTo(0, top);
      }      
    },
    mounted() {
      this.culture = window.culture ? window.culture : 'en-US'; 
      this.nodeid = window.nodeid ? window.nodeid : 0;

      // Infinite scroll replaced with button
      //document.addEventListener('scroll', this.checkLoadMore);

      if (this.currentQuery.category) {
       // this.doSearch(); // shouldn't have to do this upon load, because the items should already be loaded and filtered
      }
    }    
  }
</script>
